import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Div from "@jumbo/shared/Div";
import VoucherForm, {
  formatPriceTwoDecimals,
  getEmptyVoucherSeries,
} from "./components/VoucherForm";
import { getAccessSystemVoucherById } from "app/services/api/accessSystemSettings";
import { VOUCHERS_MANAGEMENT_BASE_PATH } from "./vouchersConstants";
import {
  getVouchersData,
  persistVoucherSeries,
} from "./vouchersDataStore";
import { getLoggedInCustomerId } from "./accessSystemLoggedInCustomer";
import { normalizeProductIdsFromVoucherApi } from "./voucherTicketProducts";

/**
 * Maps GET /api/v1/access-system/vouchers/:id body to VoucherForm initial state.
 * API may use `_id` and `codes`; the form uses `id` and `vouchers`.
 */
function mapAccessSystemVoucherDetailToFormValues(apiData, routeVoucherId) {
  if (!apiData || typeof apiData !== "object") {
    return getEmptyVoucherSeries();
  }
  const rawId = apiData._id ?? apiData.id ?? routeVoucherId;
  const id = rawId != null && String(rawId).trim() !== "" ? String(rawId) : null;
  const rawCodes = Array.isArray(apiData.codes)
    ? apiData.codes
    : Array.isArray(apiData.vouchers)
      ? apiData.vouchers
      : [];
  const vouchers = rawCodes.map((c) => ({
    code: c.code != null ? String(c.code) : "",
    status: c.status != null ? String(c.status) : "ready",
  }));
  let priceStr = "";
  if (apiData.price != null && apiData.price !== "") {
    const pn =
      typeof apiData.price === "number"
        ? apiData.price
        : parseFloat(String(apiData.price).replace(",", "."));
    if (!Number.isNaN(pn)) {
      priceStr = formatPriceTwoDecimals(pn);
    }
  }
  const productIds = normalizeProductIdsFromVoucherApi(apiData);
  return {
    id,
    name: apiData.name ?? "",
    productIds,
    ticketProduct: apiData.ticketProduct != null ? String(apiData.ticketProduct) : "",
    price: priceStr,
    validUntil: apiData.validUntil ?? "",
    status: apiData.status ?? "active",
    vouchers,
  };
}

/**
 * Create / edit voucher series — layout aligned with cart-rules EditRule (white card, tabs inside).
 */
const AccessSystemVoucherFormPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const loggedInCustomerId = getLoggedInCustomerId(getAuthUser);
  const { voucherId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isCreate = location.pathname.endsWith("/new");

  const [, bumpAfterSave] = React.useState(0);

  /** Edit/detail: GET voucher by id. Create `/new`: no fetch — show empty form immediately. */
  const [detailLoading, setDetailLoading] = React.useState(() => !isCreate);
  const [loadError, setLoadError] = React.useState(false);
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    if (isCreate || !voucherId) {
      setDetailLoading(false);
      setLoadError(false);
      setDetail(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setLoadError(false);
    setDetail(null);
    (async () => {
      try {
        const res = await getAccessSystemVoucherById(voucherId, {
          customerId: loggedInCustomerId ?? undefined,
        });
        if (cancelled) return;
        if (res && res.success === true && res.data != null) {
          setDetail(res.data);
          setLoadError(false);
        } else {
          setDetail(null);
          setLoadError(true);
        }
      } catch (_e) {
        if (!cancelled) {
          setDetail(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreate, voucherId, loggedInCustomerId]);

  const initialValues = React.useMemo(() => {
    if (isCreate) return getEmptyVoucherSeries();
    if (loadError || !detail) return getEmptyVoucherSeries();
    return mapAccessSystemVoucherDetailToFormValues(detail, voucherId);
  }, [isCreate, loadError, detail, voucherId]);

  const listTitle = t("sidebar.menuItem.accessSystemVouchers", {
    defaultValue: "Správa poukážok",
  });

  const titleNew = t("pages.dsokDetails.accessSystem.vouchers.form.titleNew", {
    defaultValue: "Nový voucher",
  });

  const titleDetailFallback = t(
    "pages.dsokDetails.accessSystem.vouchers.form.titleDetail",
    { defaultValue: "Detail voucheru" },
  );

  const pageTitle = isCreate
    ? titleNew
    : detail?.name != null && String(detail.name).trim() !== ""
      ? String(detail.name)
      : titleDetailFallback;

  const loadFailedMessage = t(
    "pages.dsokDetails.accessSystem.vouchers.form.loadFailed",
    { defaultValue: "Voucher sa nepodarilo načítať." },
  );

  const saveFailedMessage = t(
    "pages.dsokDetails.accessSystem.vouchers.form.saveFailed",
    { defaultValue: "Uloženie zlyhalo." },
  );

  const handleVoucherSave = React.useCallback(
    async (payload) => {
      if (loadError) {
        return;
      }
      const base = getVouchersData();
      const list = [...(base.voucherSeries || [])];
      const isNew = !payload.id;
      const targetId = payload.id != null ? String(payload.id) : null;
      const idx = targetId
        ? list.findIndex((s) => String(s.id) === targetId)
        : -1;

      const priceNum = (() => {
        const n = parseFloat(String(payload.price ?? "").replace(",", "."));
        if (Number.isNaN(n)) return 0;
        return Math.round(n * 100) / 100;
      })();

      const productIds = Array.isArray(payload.productIds)
        ? payload.productIds
            .map((x) => (x != null ? String(x).trim() : ""))
            .filter(Boolean)
        : [];

      const merged = {
        ...(idx >= 0 ? list[idx] : {}),
        name: payload.name,
        productIds,
        ticketProduct:
          payload.ticketProduct != null ? String(payload.ticketProduct) : "",
        price: priceNum,
        validUntil: payload.validUntil,
        status: payload.status,
        vouchers: Array.isArray(payload.vouchers) ? payload.vouchers : [],
        ...(loggedInCustomerId
          ? { customerId: loggedInCustomerId }
          : {}),
      };

      if ("productId" in merged) {
        delete merged.productId;
      }

      const row = isNew ? merged : { ...merged, id: targetId };

      try {
        const { apiEntity } = await persistVoucherSeries(row);
        bumpAfterSave((n) => n + 1);
        enqueueSnackbar(
          t("notifications.savedSuccessfully", {
            defaultValue: "Saved successfully",
          }),
          { variant: "success" },
        );

        if (isNew) {
          const newId =
            apiEntity != null && apiEntity.id != null
              ? String(apiEntity.id)
              : null;
          if (newId) {
            navigate(`${VOUCHERS_MANAGEMENT_BASE_PATH}/${newId}`);
          } else {
            navigate(VOUCHERS_MANAGEMENT_BASE_PATH);
          }
        }
      } catch (err) {
        const fromApi = err?.response?.data?.message;
        const detail =
          typeof fromApi === "string" && fromApi.trim()
            ? fromApi.trim()
            : saveFailedMessage;
        enqueueSnackbar(detail, { variant: "error" });
      }
    },
    [
      enqueueSnackbar,
      loadError,
      loggedInCustomerId,
      navigate,
      saveFailedMessage,
      t,
    ],
  );

  const handleVoucherCancel = React.useCallback(() => {
    navigate(VOUCHERS_MANAGEMENT_BASE_PATH);
  }, [navigate]);

  const showVoucherForm =
    isCreate ||
    (voucherId && !detailLoading && (detail != null || loadError));

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Link to={VOUCHERS_MANAGEMENT_BASE_PATH} color="inherit">
          {listTitle}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      <Div
        sx={{
          display: "flex",
          width: "100%",
          p: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
            {pageTitle}
          </Typography>

          {loadError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loadFailedMessage}
            </Alert>
          ) : null}

          {detailLoading && !isCreate && voucherId ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={240}
            >
              <CircularProgress />
            </Box>
          ) : null}

          {showVoucherForm ? (
            <VoucherForm
              key={isCreate ? "create" : voucherId}
              mode={isCreate ? "create" : "edit"}
              initialValues={initialValues}
              onSave={handleVoucherSave}
              onCancel={handleVoucherCancel}
            />
          ) : null}
        </Box>
      </Div>
    </React.Fragment>
  );
};

export default AccessSystemVoucherFormPage;
